const { Type } = require('@sinclair/typebox');
const Blockly = require('blockly');
const setupAddCodeProperties = require('../../../../helpers/addCodeProperties');

const TOOLTIP = 'Criteria element for use on FORMS to document a constrain on a plain text field';

/**
 * @param {*} color
 * @param {import('blockly').Block} blocklyBlock reference to the block as a whole
 * @param {Object} mdcBlock Meta Data Configuration block. the group definition from blockGroupConfiguration.js
 * @param {import('blockly').Input} line1OfInputs First line of inputs, already including the Block name and the title field
 */
function addBlockSpecificFields(color, blocklyBlock, mdcBlock, line1OfInputs) {
    blocklyBlock.setTooltip(TOOLTIP);
    //set inputs to be arranged horizontally.
    //this gets the security modifier in line with the fields
    //because we only have one line of fields, that looks better
    blocklyBlock.setInputsInline(true);

    line1OfInputs
        .appendField('  Property Name:')
        .appendField(new Blockly.FieldTextInput('title'), 'propertyName')
        .appendField('  Min Length:')
        .appendField(new Blockly.FieldNumber('0', Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1), 'minLength')
        .appendField('  Max Length:')
        .appendField(new Blockly.FieldNumber('100', Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, 1), 'maxLength')
        .appendField('  Validation Pattern:')
        .appendField(new Blockly.FieldTextInput(''), 'validationPattern')
        .appendField('  Tooltip:')
        .appendField(new Blockly.FieldTextInput(''), 'tooltip');
}

/**
 * @param {{[key: string]: any}} code
 * @param {import('blockly').Block} blocklyBlock
 * @return {{[key: string]: any}}
 */
function convertCustomFieldsToCode(code, blocklyBlock) {
    const addCodeProperties = setupAddCodeProperties(code, blocklyBlock);

    addCodeProperties([
        'propertyName',
        { key: 'pattern', value: 'validationPattern' },
        { key: 'minLength', handler: parseInt },
        { key: 'maxLength', handler: parseInt },
        'tooltip'
    ]);

    return code;
}

/** Builds the criteria schema fragment for the ShortTextCriteria workflow element. */
function asDataDictionary(hNode, context) {
    const { minLength, maxLength, pattern, propertyName, required = false } = hNode;

    // we could be on an update form, or on a new form, or nested somewhere
    // We just don't know what accessContext this would be enabling, nor how it is applied on a NS/REL
    // Security Modifiers (which would disabled certain accessContext) are processed generically
    // All we know is that Whatever propertyName is on this Criteria block
    // and how it is formatted.

    // Build string schema options with validation constraints
    const stringOptions = {
        'x-dataType': 'string',
        'x-optional': !required,
        minLength,
        maxLength
    };

    // Add pattern if provided (pattern is a standard JSON Schema field)
    if (pattern) {
        stringOptions.pattern = pattern;
    }

    // Create TypeBox string schemas for each operator
    const eqSchema = Type.Optional(
        Type.String({
            ...stringOptions,
            title: 'Test for equality',
            'x-propertyName': '_eq'
        })
    );

    const neqSchema = Type.Optional(
        Type.String({
            ...stringOptions,
            title: 'Test for inequality',
            'x-propertyName': '_neq'
        })
    );

    const containsSchema = Type.Optional(
        Type.String({
            ...stringOptions,
            title: 'Test property for containing specified value',
            'x-propertyName': '_contains'
        })
    );

    const startsWithSchema = Type.Optional(
        Type.String({
            ...stringOptions,
            title: 'Test property to start with specified value',
            'x-propertyName': '_startsWith'
        })
    );

    // Create main criteria object containing all operators
    const criteriaSchema = Type.Object(
        {
            _eq: eqSchema,
            _neq: neqSchema,
            _contains: containsSchema,
            _startsWith: startsWithSchema
        },
        {
            'x-id': hNode.id,
            title: hNode.title,
            'x-tooltip': hNode.tooltip,
            'x-propertyName': propertyName,
            'x-dataType': 'object',
            'x-optional': !required,
            additionalProperties: false
        }
    );

    const partialTypeDefinition = {
        [propertyName]: required ? criteriaSchema : Type.Optional(criteriaSchema)
    };

    return [{}, partialTypeDefinition];
}

module.exports = { addBlockSpecificFields, convertCustomFieldsToCode, asDataDictionary };
